import { useState, useCallback, useEffect, useRef } from "react";

/**
 * 店鋪庫存管理系統側邊選單 Hook
 *
 * @param {Object} options - 配置選項
 * @param {boolean} [options.defaultOpen=false] - 預設是否展開
 * @param {number} [options.animationDuration=300] - 動畫時長（毫秒）
 * @param {number} [options.navWidth=240] - 選單寬度（px）
 * @param {Function} [options.onOpen] - 選單開啟時的回調函數
 * @param {Function} [options.onClose] - 選單關閉時的回調函數
 * @param {Function} [options.onToggle] - 選單切換時的回調函數，接收新的狀態作為參數
 * @returns {Object} 狀態與方法
 */
const useInventoryNav = ({
	defaultOpen = false,
	animationDuration = 300,
	navWidth = 240,
	onOpen,
	onClose,
	onToggle,
} = {}) => {
	const [isOpen, setIsOpen] = useState(defaultOpen);
	const [isAnimating, setIsAnimating] = useState(false);
	const animationTimeoutRef = useRef(null);

	// 切換選單狀態
	const toggle = useCallback(() => {
		setIsOpen(prev => {
			const newState = !prev;
			// 調用外部回調
			if (onToggle) onToggle(newState);
			if (newState) {
				if (onOpen) onOpen();
			} else {
				if (onClose) onClose();
			}
			return newState;
		});
		setIsAnimating(true);

		// 清除之前的 timeout
		if (animationTimeoutRef.current) {
			clearTimeout(animationTimeoutRef.current);
		}

		// 動畫結束後清除動畫狀態
		animationTimeoutRef.current = setTimeout(() => {
			setIsAnimating(false);
		}, animationDuration);
	}, [animationDuration, onOpen, onClose, onToggle]);

	// 開啟選單
	const open = useCallback(() => {
		if (!isOpen) {
			setIsOpen(true);
			setIsAnimating(true);
			// 調用外部回調
			if (onOpen) onOpen();
			if (onToggle) onToggle(true);

			if (animationTimeoutRef.current) {
				clearTimeout(animationTimeoutRef.current);
			}

			animationTimeoutRef.current = setTimeout(() => {
				setIsAnimating(false);
			}, animationDuration);
		}
	}, [isOpen, animationDuration, onOpen, onToggle]);

	// 關閉選單
	const close = useCallback(() => {
		if (isOpen) {
			setIsOpen(false);
			setIsAnimating(true);
			// 調用外部回調
			if (onClose) onClose();
			if (onToggle) onToggle(false);

			if (animationTimeoutRef.current) {
				clearTimeout(animationTimeoutRef.current);
			}

			animationTimeoutRef.current = setTimeout(() => {
				setIsAnimating(false);
			}, animationDuration);
		}
	}, [isOpen, animationDuration, onClose, onToggle]);

	// 清理 timeout
	useEffect(() => {
		return () => {
			if (animationTimeoutRef.current) {
				clearTimeout(animationTimeoutRef.current);
			}
		};
	}, []);

	// 計算是否已展開（用於 CSS 動畫）
	const isExpanded = isOpen && !isAnimating;

	return {
		// 狀態
		isOpen,
		isAnimating,
		isExpanded,

		// 方法
		toggle,
		open,
		close,

		// 配置
		animationDuration,
		navWidth,
	};
};

export default useInventoryNav;
